import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FloppyDisk, X, UploadSimple, Sparkle, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Stream } from "@cloudflare/stream-react";
import { uploadToCloudflareStreamTUS, TUS_THRESHOLD_BYTES } from "../lib/streamTus";

/**
 * Generic content-type CMS panel.
 *
 * props.config = {
 *   publicPath:  "/reels",
 *   adminPath:   "/admin/reels",
 *   itemLabel:   "Reel",
 *   blank:       { title:"", category:"", ... },
 *   columns:     [{ key:"title", label:"Title" }, ...],   // shown in list table
 *   fields:      [{ key:"title", label:"Title", type:"text", required:true, placeholder, rows, options }, ...],
 * }
 */
export default function CMSPanel({ token, config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // null | {} | item
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { "X-Admin-Token": token };

  const refresh = async () => {
    setLoading(true);
    try {
      // Admin views can request the full list (incl. unpublished/archived) via listAllPath.
      const { data } = await axios.get(`${API}${config.listAllPath || config.publicPath}`);
      setItems(data);
    } catch {
      toast.error(`Could not load ${config.itemLabel} list`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setEditing({ __new: true });
    setDraft({ ...config.blank });
  };

  const startEdit = (item) => {
    setEditing(item);
    setDraft({ ...config.blank, ...item });
  };

  const cancel = () => { setEditing(null); setDraft(null); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      // strip server-only fields when sending
      delete payload.id;
      delete payload.created_at;
      // Normalize arrays from textarea (one per line)
      config.fields.forEach((f) => {
        if (f.type === "lines") {
          const v = payload[f.key];
          if (typeof v === "string") {
            payload[f.key] = v.split("\n").map((s) => s.trim()).filter(Boolean);
          }
        }
        if (f.type === "number") payload[f.key] = Number(payload[f.key] ?? 0);
      });
      if (editing.__new) {
        await axios.post(`${API}${config.adminPath}`, payload, { headers });
        toast.success(`${config.itemLabel} created`);
      } else {
        await axios.put(`${API}${config.adminPath}/${editing.id}`, payload, { headers });
        toast.success(`${config.itemLabel} updated`);
      }
      cancel();
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : `Validation error`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[config.columns[0].key]}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}${config.adminPath}/${item.id}`, { headers });
      toast.success(`${config.itemLabel} deleted`);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleArchive = async (item) => {
    const next = !item.archived;
    try {
      await axios.post(`${API}${config.adminPath}/${item.id}/archive?archived=${next}`, {}, { headers });
      toast.success(next ? `${config.itemLabel} archived — hidden from the site` : `${config.itemLabel} re-enabled`);
      refresh();
    } catch {
      toast.error("Action failed");
    }
  };

  const linesToString = (v) => (Array.isArray(v) ? v.join("\n") : (v || ""));

  // File upload helper for fields with `upload: 'image' | 'video'`
  const inputRefs = useRef({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [streamProgress, setStreamProgress] = useState({}); // { fieldKey: { pct, state } }

  const handleFile = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(field.key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      setDraft((d) => ({ ...d, [field.key]: data.url }));
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploadingKey(null);
      // Reset input so picking the same file again still triggers onChange
      if (inputRefs.current[field.key]) inputRefs.current[field.key].value = "";
    }
  };

  /**
   * Cloudflare Stream upload — server-proxied via wisewit.ai/api/admin/stream/migrate.
   *
   * The old "direct creator upload" path (browser → upload.cloudflarestream.com)
   * broke on some networks with `ERR_CERT_AUTHORITY_INVALID` — corporate TLS
   * inspection, VPN clients, or client-clock skew can invalidate Cloudflare's
   * cert path.  By posting to our own backend first (which the browser already
   * trusts) we bypass every one of those issues; the backend then relays the
   * bytes to Cloudflare from the datacenter with no cert drama.
   *
   *   1. POST file to /admin/stream/migrate — returns { uid, state }.
   *   2. Poll /admin/stream/{uid}/status every 2s until state === "ready".
   *   3. Write the HLS URL back to the field; overwrite cover_image with the
   *      auto-generated Cloudflare thumbnail.
   *
   * Note: Nginx `client_max_body_size` on the `/api/` block must be at least
   * as large as the biggest video you plan to publish (recommend 500m).
   */
  const handleStreamUpload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(field.key);
    setStreamProgress((s) => ({ ...s, [field.key]: { pct: 0, state: "uploading" } }));

    // ---- Route large files through TUS resumable direct-to-CF upload ----
    if (file.size >= TUS_THRESHOLD_BYTES) {
      try {
        const result = await uploadToCloudflareStreamTUS(file, token, (p) =>
          setStreamProgress((s) => ({ ...s, [field.key]: p })));
        setDraft((d) => ({
          ...d,
          [field.key]: result.hls_url,
          cover_image: result.thumbnail_url || d.cover_image,
        }));
        toast.success("Large file uploaded via TUS resumable — Cloudflare Stream ready. Click Save.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "TUS upload failed.");
      } finally {
        setUploadingKey(null);
        setStreamProgress((s) => { const c = { ...s }; delete c[field.key]; return c; });
        if (inputRefs.current[field.key]) inputRefs.current[field.key].value = "";
      }
      return;
    }

    // ---- Small file (<200 MB): server-proxied migrate path ----
    try {
      // Step 1: upload the file through OUR backend (server-proxied)
      const fd = new FormData();
      fd.append("file", file);
      const { data: migrated } = await axios.post(`${API}/admin/stream/migrate`, fd, {
        headers: { "X-Admin-Token": token },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setStreamProgress((s) => ({ ...s, [field.key]: { pct, state: "uploading" } }));
        },
        // Nginx / VM proxies can be slow with big files — no timeout here
        timeout: 0,
      });
      const uid = migrated?.uid;
      if (!uid) throw new Error("Backend didn't return a Cloudflare video UID.");

      // Step 2: poll backend for readiness (encoding takes 10–60s)
      setStreamProgress((s) => ({ ...s, [field.key]: { pct: 100, state: "encoding" } }));
      let ready = null;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: st } = await axios.get(`${API}/admin/stream/${uid}/status`, { headers: { "X-Admin-Token": token } });
          if (st.ready) { ready = st; break; }
          if (st.state === "error") throw new Error("Cloudflare reported encoding error.");
        } catch (pollErr) {
          if (pollErr?.response?.status === 404) throw pollErr;
          // transient network flake — keep polling
        }
      }
      if (!ready) throw new Error("Encoding timed out after ~2 min. Check Cloudflare dashboard.");

      // Step 3: write the URLs back into the draft
      setDraft((d) => ({
        ...d,
        [field.key]: ready.hls_url,
        cover_image: ready.thumbnail_url || d.cover_image,
      }));
      toast.success("Video ready on Cloudflare Stream — URLs saved. Click Save to finish.");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Stream upload failed";
      // Common-cause hints
      let hint = "";
      if (typeof detail === "string" && /413|Request Entity Too Large|Payload/i.test(detail)) {
        hint = " · Bump Nginx client_max_body_size on /api/ to 500m and reload.";
      } else if (typeof detail === "string" && /CERT|SSL|handshake/i.test(detail)) {
        hint = " · TLS problem — check the VM's system time (`sudo timedatectl`).";
      }
      toast.error((typeof detail === "string" ? detail : "Stream upload failed") + hint);
    } finally {
      setUploadingKey(null);
      setStreamProgress((s) => { const c = { ...s }; delete c[field.key]; return c; });
      if (inputRefs.current[field.key]) inputRefs.current[field.key].value = "";
    }
  };

  return (
    <div data-testid={`cms-${config.itemLabel.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-overline">◢ {config.itemLabel} ({items.length})</div>
        <button data-testid={`cms-new-${config.itemLabel.toLowerCase().replace(/\s+/g,'-')}`} onClick={startNew} className="btn-primary"><Plus size={14} weight="bold" /> New</button>
      </div>

      {loading && <div className="text-[#8E8E93] text-sm px-4 py-6">Loading…</div>}

      {!loading && (
        <div className="border border-[#2A2A2A]">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
            {config.columns.map((c, i) => (
              <div key={c.key} className={`col-span-${c.span || (i === 0 ? 4 : 3)}`}>{c.label}</div>
            ))}
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {items.length === 0 && <div className="px-4 py-10 text-[#555] text-sm text-center">No {config.itemLabel.toLowerCase()} yet — click <strong className="text-white">New</strong>.</div>}
          {items.map((it) => (
            <div key={it.id} className={`grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] text-sm items-center ${it.archived ? "opacity-55" : ""}`} data-testid={`cms-row-${it.id}`}>
              {config.columns.map((c, i) => (
                <div key={c.key} className={`col-span-${c.span || (i === 0 ? 4 : 3)} ${c.mono ? "font-mono text-xs text-[#DFFF00]" : "text-white truncate"}`} title={String(it[c.key] ?? "")}>
                  {i === 0 && it.archived && (
                    <span className="mr-2 align-middle inline-block px-1.5 py-0.5 font-mono text-[9px] tracking-[0.16em] uppercase bg-[#2A2A2A] text-[#FF4500] border border-[#FF4500]/40" data-testid={`cms-archived-badge-${it.id}`}>Archived</span>
                  )}
                  {Array.isArray(it[c.key]) ? it[c.key].join(", ") : String(it[c.key] ?? "—")}
                </div>
              ))}
              <div className="col-span-2 flex justify-end gap-2">
                {config.archivable && (
                  <button
                    onClick={() => toggleArchive(it)}
                    className={it.archived ? "text-[#8E8E93] hover:text-[#DFFF00]" : "text-[#8E8E93] hover:text-[#FF4500]"}
                    title={it.archived ? "Re-enable (unarchive)" : "Archive (hide from site)"}
                    data-testid={`cms-archive-${it.id}`}
                  >
                    {it.archived ? <ArrowCounterClockwise size={16} /> : <Archive size={16} />}
                  </button>
                )}
                <button onClick={() => startEdit(it)} className="text-[#8E8E93] hover:text-white" title="Edit"><PencilSimple size={16} /></button>
                <button onClick={() => remove(it)} className="text-[#8E8E93] hover:text-[#FF4500]" title="Delete"><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && draft && (
        <div className="fixed inset-0 z-[60] bg-[#050505]/85 backdrop-blur flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" onClick={cancel}>
          <div className="bg-[#0a0a0a] border border-[#2A2A2A] w-full max-w-2xl my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-14 border-b border-[#2A2A2A]">
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#8E8E93]">
                {editing.__new ? `New ${config.itemLabel}` : `Edit ${config.itemLabel}`}
              </div>
              <button onClick={cancel} className="text-[#8E8E93]"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {config.aiCopy && (
                <AICopyHelper
                  draft={draft}
                  setDraft={setDraft}
                  config={config.aiCopy}
                />
              )}
              {config.fields.map((f) => (
                <div key={f.key}>
                  <label className="ww-label">{f.label}{f.required && <span className="text-[#FF4500]"> *</span>}</label>
                  {f.type === "textarea" || f.type === "lines" ? (
                    <textarea
                      rows={f.rows || 4}
                      value={f.type === "lines" ? linesToString(draft[f.key]) : (draft[f.key] ?? "")}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      placeholder={f.placeholder || (f.type === "lines" ? "One item per line" : "")}
                      className="ww-input resize-none"
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="ww-input bg-[#050505]"
                    >
                      <option value="">—</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "color" ? (
                    <div className="flex items-center gap-3">
                      <input type="color" value={draft[f.key] || "#FF4500"} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} className="w-12 h-10 bg-transparent border border-[#2A2A2A] cursor-pointer" />
                      <input type="text" value={draft[f.key] || ""} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} className="ww-input" placeholder="#FF4500" />
                    </div>
                  ) : f.type === "boolean" ? (
                    <label className="flex items-start gap-3 border border-[#2A2A2A] p-3 cursor-pointer hover:border-[#FF4500] transition-colors" data-testid={`cms-boolean-${f.key}`}>
                      <input
                        type="checkbox"
                        checked={!!draft[f.key]}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.checked })}
                        className="mt-1"
                      />
                      <span className="text-sm text-[#F5F5F5]">
                        {draft[f.key] ? "On" : "Off"}
                        {f.help && <span className="block text-[11px] text-[#8E8E93] mt-1">{f.help}</span>}
                      </span>
                    </label>
                  ) : (
                    <>
                      <input
                        type={f.type === "number" ? "number" : (f.type === "url" ? "url" : "text")}
                        value={draft[f.key] ?? ""}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        placeholder={f.placeholder || ""}
                        className="ww-input"
                      />
                      {f.upload && (
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <input
                            ref={(el) => (inputRefs.current[f.key] = el)}
                            type="file"
                            accept={f.upload === "image" ? "image/*" : (f.upload === "video" ? "video/*,image/*" : (f.upload === "stream" ? "video/*" : "*/*"))}
                            onChange={(e) => (f.upload === "stream" ? handleStreamUpload(f, e) : handleFile(f, e))}
                            className="hidden"
                            data-testid={`cms-file-${f.key}`}
                          />
                          <button
                            type="button"
                            onClick={() => inputRefs.current[f.key]?.click()}
                            disabled={uploadingKey === f.key}
                            className="btn-secondary text-[10px] py-2 px-3"
                            data-testid={`cms-upload-btn-${f.key}`}
                          >
                            <UploadSimple size={12} /> {uploadingKey === f.key
                              ? (f.upload === "stream" ? "Uploading to Cloudflare…" : "Uploading…")
                              : (f.upload === "stream" ? "Upload video to Cloudflare Stream" : `Upload ${f.upload}`)}
                          </button>
                          {f.upload === "stream" && streamProgress[f.key] && (
                            <div className="flex-1 min-w-[140px] max-w-xs" data-testid={`cms-stream-progress-${f.key}`}>
                              <div className="h-1 bg-[#1A1A1A] overflow-hidden">
                                <div
                                  className="h-full bg-[#DFFF00] transition-[width] duration-200"
                                  style={{ width: `${streamProgress[f.key].pct}%` }}
                                />
                              </div>
                              <div className="mt-1 font-mono text-[9px] tracking-[0.18em] uppercase text-[#8E8E93]">
                                {streamProgress[f.key].state === "uploading" && `Uploading ${streamProgress[f.key].pct}%`}
                                {streamProgress[f.key].state === "encoding" && "Encoding on Cloudflare (10–60s)…"}
                              </div>
                            </div>
                          )}
                          {draft[f.key] && (
                            <span className="font-mono text-[10px] text-[#DFFF00] truncate max-w-[60%]" title={draft[f.key]}>
                              {draft[f.key]}
                            </span>
                          )}
                        </div>
                      )}
                      {f.upload === "image" && draft[f.key] && (
                        <div className="mt-2 w-32 h-20 border border-[#2A2A2A] overflow-hidden bg-[#0a0a0a]">
                          <img
                            src={mediaUrl(draft[f.key])}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.opacity = "0.2"; e.target.title = "Failed to load — check reverse proxy /api/uploads/* mapping"; }}
                          />
                        </div>
                      )}
                      {(f.upload === "video" || f.upload === "stream") && draft[f.key] && (
                        <div className="mt-2 w-full max-w-sm border border-[#2A2A2A] overflow-hidden bg-[#0a0a0a]" data-testid={`cms-video-preview-${f.key}`}>
                          {/\.m3u8(\?|#|$)/i.test(draft[f.key]) || /cloudflarestream\.com/i.test(draft[f.key]) ? (
                            <StreamHLSPlayer src={draft[f.key]} />
                          ) : /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(draft[f.key]) ? (
                            <video
                              src={mediaUrl(draft[f.key])}
                              controls
                              preload="metadata"
                              playsInline
                              className="w-full h-auto block"
                              onError={() => toast.error("Video preview failed — open the URL in a new tab to verify the file is reachable.")}
                            />
                          ) : (
                            <img
                              src={mediaUrl(draft[f.key])}
                              alt=""
                              className="w-full h-auto block"
                              onError={(e) => { e.target.style.opacity = "0.2"; }}
                            />
                          )}
                          <div className="font-mono text-[10px] text-[#8E8E93] px-2 py-1 border-t border-[#2A2A2A] truncate" title={draft[f.key]}>
                            {draft[f.key]}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {f.help && <div className="mt-1 font-mono text-[10px] text-[#555]">{f.help}</div>}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 h-16 border-t border-[#2A2A2A]">
              <button onClick={cancel} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="cms-save" className="btn-primary disabled:opacity-50">
                <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Cloudflare Stream HLS player.  Accepts either the full HLS manifest URL
 * (`.../{uid}/manifest/video.m3u8`) or a raw video UID — extracts the UID and
 * hands it to @cloudflare/stream-react, which handles portrait vs landscape,
 * adaptive bitrate, and mobile playback automatically.
 */
export function StreamHLSPlayer({ src, autoplay = false, poster = "" }) {
  const uidMatch = String(src || "").match(/cloudflarestream\.com\/([a-f0-9]{32})/i);
  const uid = uidMatch ? uidMatch[1] : String(src || "").trim();
  if (!uid) return null;
  return (
    <div className="w-full bg-[#050505]" data-testid="stream-hls-player">
      <Stream
        controls
        src={uid}
        responsive={true}
        preload="metadata"
        autoplay={autoplay}
        playsInline
        poster={poster || undefined}
      />
    </div>
  );
}


/**
 * Inline AI helper used by configs that pass `config.aiCopy`.
 *
 * Expected shape:
 *   {
 *     endpoint: "/ai/atlas-copy",
 *     titleField: "title",
 *     bulletsField: "bullets",       // optional, sent as extra context
 *     intoFields: ["short", "detail"]
 *   }
 */
function AICopyHelper({ draft, setDraft, config }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const titleVal = draft?.[config.titleField] || "";
  const bulletsRaw = draft?.[config.bulletsField];
  const bullets = Array.isArray(bulletsRaw)
    ? bulletsRaw
    : (typeof bulletsRaw === "string" ? bulletsRaw.split("\n").filter(Boolean) : []);

  const generate = async () => {
    if (!titleVal.trim()) {
      toast.error("Add a title first so the AI knows the capability.");
      return;
    }
    if (!notes.trim()) {
      toast.error("Paste a few rough notes about this capability.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}${config.endpoint}`, {
        title: titleVal,
        notes,
        bullets,
      });
      const patch = {};
      if (config.intoFields.includes("short") && data.short) patch.short = data.short;
      if (config.intoFields.includes("detail") && data.detail) patch.detail = data.detail;
      setDraft({ ...draft, ...patch });
      toast.success("Copy generated. Review and edit below before saving.");
      setOpen(false);
      setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-dashed border-[#DFFF00]/40 bg-[#DFFF00]/[0.03] p-4"
      data-testid="ai-copy-helper"
    >
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ai-copy-open"
          className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00] hover:text-white transition-colors"
        >
          <Sparkle size={14} weight="fill" /> Generate from notes (AI)
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">
              <Sparkle size={12} weight="fill" /> Generate from notes
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-[#8E8E93]">
              <X size={14} />
            </button>
          </div>
          <p className="font-mono text-[10px] text-[#8E8E93] leading-relaxed">
            Paste 3-4 rough engineer notes about <span className="text-white">{titleVal || "this capability"}</span>.
            GPT-5.2 will turn them into a polished short headline + a 2-sentence detail paragraph in Wisewit's voice.
            You'll get to review and edit before saving.
          </p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            data-testid="ai-copy-notes"
            placeholder={'e.g. "we deploy YOLO at edge, 8 cameras, MES write-back, retraining weekly, FDA-class lighting tolerant"'}
            className="ww-input resize-none"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={generate}
              disabled={busy}
              data-testid="ai-copy-generate"
              className="btn-primary disabled:opacity-50"
            >
              {busy ? "Generating…" : "Generate"}
            </button>
            <span className="font-mono text-[9px] text-[#555]">
              Fills: {config.intoFields.join(" + ")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

